import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, Divider } from '@mui/material';
import { FileText } from 'lucide-react';
import { humanizeKey } from '../../utils/formatters/stringUtils';
import { formatValue, isDateField } from '../../utils/formatters/valueUtils';
import { formatDateBr } from '../../utils/formatters/dateUtils';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import ReportWidget from './ReportWidget';

const THEMES_ORDER = [
  'uc',
  'pra',
  'biomas',
  'aldeias',
  'florestas',
  'mineracao',
  'quilombolas',
  'assentamentos',
  'embargos_ibama',
  'terras_indigenas',
];

const THEME_LABELS = {
  uc: 'Unidades de Conservação',
  pra: 'Plano de Recuperação Ambiental (PRA)',
  biomas: 'Biomas',
  aldeias: 'Aldeias',
  florestas: 'Florestas Públicas',
  mineracao: 'Mineração',
  quilombolas: 'Terras Quilombolas',
  assentamentos: 'Assentamentos INCRA',
  embargos_ibama: 'Embargos do IBAMA',
  terras_indigenas: 'Terras Indígenas',
};

// Mapeia o themeKey (usado no domínio de config) para as chaves de layout
// definidas no seed (section_key / widget_key).
const THEME_LAYOUT_CONFIG = {
  biomas: {
    sectionKey: 'sobreposicao_biomas',
    widgetKey: 'sobreposicao_biomas_main',
  },
  uc: {
    sectionKey: 'sobreposicao_uc',
    widgetKey: 'sobreposicao_uc_main',
  },
  florestas: {
    sectionKey: 'sobreposicao_florestas_publicas',
    widgetKey: 'sobreposicao_florestas_publicas_main',
  },
  terras_indigenas: {
    sectionKey: 'sobreposicao_terras_indigenas',
    widgetKey: 'sobreposicao_terras_indigenas_main',
  },
  aldeias: {
    sectionKey: 'sobreposicao_aldeias',
    widgetKey: 'sobreposicao_aldeias_main',
  },
  quilombolas: {
    sectionKey: 'sobreposicao_terras_quilombolas',
    widgetKey: 'sobreposicao_terras_quilombolas_main',
  },
  assentamentos: {
    sectionKey: 'sobreposicao_assentamentos_incra',
    widgetKey: 'sobreposicao_assentamentos_incra_main',
  },
  mineracao: {
    sectionKey: 'sobreposicao_mineracao',
    widgetKey: 'sobreposicao_mineracao_main',
  },
  embargos_ibama: {
    sectionKey: 'sobreposicao_embargos_ibama',
    widgetKey: 'sobreposicao_embargos_ibama_main',
  },
  pra: {
    sectionKey: 'sobreposicao_pra',
    widgetKey: 'sobreposicao_pra_main',
  },
};

const buildGroupsByImovel = (rawList) => {
  if (!Array.isArray(rawList)) return [];

  const map = new Map();

  rawList.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const cod = item.cod_imovel || '—';
    const temas = item.temas && typeof item.temas === 'object' ? item.temas : {};
    const current = map.get(cod) || { cod_imovel: cod, temas: {} };

    THEMES_ORDER.forEach((key) => {
      const arr = Array.isArray(temas[key])
        ? temas[key].filter((t) => t && typeof t === 'object')
        : [];
      const existing = Array.isArray(current.temas[key]) ? current.temas[key] : [];
      if (arr.length > 0) {
        current.temas[key] = [...existing, ...arr];
      } else if (!current.temas[key]) {
        current.temas[key] = [];
      }
    });

    map.set(cod, current);
  });

  return Array.from(map.values());
};

const formatThemeValue = (key, value) => {
  const keyLower = String(key || '').toLowerCase();

  if (isDateField(keyLower)) {
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'string') {
        const raw = value.trim();

        if (/^\d{4}$/.test(raw)) {
          return raw;
        }

        if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(raw)) {
          const sep = raw.includes('-') ? '-' : '/';
          const [dd, mm, yyyy] = raw.split(sep);
          const normalized = `${dd}/${mm}/${yyyy}`;
          const formatted = formatDateBr(normalized);
          if (formatted) return formatted;
        }

        if (/^\d{2}[-/]\d{4}$/.test(raw)) {
          const [mm, yyyy] = raw.includes('-') ? raw.split('-') : raw.split('/');
          const normalized = `01/${mm}/${yyyy}`;
          const formatted = formatDateBr(normalized);
          if (formatted) return formatted;
        }

        if (/^\d{4}[-/]\d{2}$/.test(raw)) {
          const [yyyy, mm] = raw.includes('-') ? raw.split('-') : raw.split('/');
          const normalized = `01/${mm}/${yyyy}`;
          const formatted = formatDateBr(normalized);
          if (formatted) return formatted;
        }

        if (/^\d{6}$/.test(raw)) {
          const yearFirst = parseInt(raw.slice(0, 4), 10);
          const monthLast = parseInt(raw.slice(4, 6), 10);
          if (yearFirst >= 1900 && monthLast >= 1 && monthLast <= 12) {
            const normalized = `01/${raw.slice(4, 6)}/${raw.slice(0, 4)}`;
            const formatted = formatDateBr(normalized);
            if (formatted) return formatted;
          }

          const monthFirst = parseInt(raw.slice(0, 2), 10);
          const yearLast = parseInt(raw.slice(2, 6), 10);
          if (monthFirst >= 1 && monthFirst <= 12 && yearLast >= 1900) {
            const normalized = `01/${raw.slice(0, 2)}/${raw.slice(2, 6)}`;
            const formatted = formatDateBr(normalized);
            if (formatted) return formatted;
          }
        }
      }

      const formattedDate = formatDateBr(value);
      if (formattedDate) {
        return formattedDate;
      }
    }
  }

  let display = formatValue(value);

  if (keyLower.startsWith('pct')) {
    let num;
    if (typeof value === 'number') {
      num = value;
    } else if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !Number.isNaN(Number(value))
    ) {
      num = Number(value);
    } else {
      num = null;
    }

    if (typeof num === 'number' && !Number.isNaN(num)) {
      const pct = Math.abs(num) <= 1 ? num * 100 : num;
      display = `${pct.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    }
  }

  return display;
};

export default function ReportPrintSobreposicaoDetails({ snapshot, themeKey }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  const domainEnabled = isDomainEnabled('sobreposicao');

  if (!domainEnabled) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio Sobreposição desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  if (!themeKey) {
    return null;
  }

  const rawData =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties.sobreposicao
      : null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const groups = useMemo(() => buildGroupsByImovel(rawData), [rawData]);

  const themeEnabled = isSectionEnabled('sobreposicao', themeKey);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const groupsForTheme = useMemo(() => {
    if (!themeEnabled) return [];

    return groups
      .map((group) => {
        const temas = group.temas || {};
        const registros = Array.isArray(temas[themeKey]) ? temas[themeKey] : [];
        if (!registros.length) return null;
        return {
          cod_imovel: group.cod_imovel,
          registros: registros.filter((r) => r && typeof r === 'object'),
        };
      })
      .filter(Boolean);
  }, [groups, themeEnabled, themeKey]);

  const themeLabel = getFieldLabel('sobreposicao', themeKey, THEME_LABELS[themeKey] || themeKey);

  if (!themeEnabled) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Tema de sobreposição "{themeLabel}" desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  if (!groupsForTheme.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum dado de sobreposição disponível para o tema "{themeLabel}".
        </Typography>
      </Box>
    );
  }

  const layoutConfig = THEME_LAYOUT_CONFIG[themeKey];

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FileText size={20} style={{ marginRight: 8 }} />
        <Typography variant="h6">Sobreposições - {themeLabel}</Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />
      {layoutConfig ? (
        <ReportWidget sectionKey={layoutConfig.sectionKey} widgetKey={layoutConfig.widgetKey}>
          {groupsForTheme.map((group, index) => (
            <Box
              key={group.cod_imovel || index}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Código do imóvel: {group.cod_imovel || '—'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                {group.registros.length === 1
                  ? '1 registro de sobreposição'
                  : `${group.registros.length} registros de sobreposição`}
              </Typography>

              {group.registros.map((item, itemIndex) => {
                const entries = Object.entries(item || {})
                  .filter(
                    ([field]) => field !== 'geojson' && field !== 'geometry' && field !== 'feature',
                  )
                  .filter(([field]) => isFieldEnabled('sobreposicao', themeKey, field));

                return (
                  <Card
                    key={`${group.cod_imovel || index}-${themeKey}-${itemIndex}`}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 2,
                      boxShadow: 0,
                      borderLeft: 3,
                      borderLeftColor: 'primary.main',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {`${themeLabel} • Item ${itemIndex + 1}`}
                      </Typography>

                      {entries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Sem detalhes disponíveis para este registro.
                        </Typography>
                      ) : (
                        <Grid container spacing={1.5}>
                          {entries.map(([field, value]) => (
                            <Grid item xs={12} sm={6} md={4} key={field}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('sobreposicao', field, humanizeKey(field))}
                              </Typography>
                              <ReportStyledField
                                domainKey="sobreposicao"
                                fieldKey={field}
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatThemeValue(field, value)}
                              </ReportStyledField>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ))}
        </ReportWidget>
      ) : (
        groupsForTheme.map((group, index) => (
          <Box
            key={group.cod_imovel || index}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Código do imóvel: {group.cod_imovel || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {group.registros.length === 1
                ? '1 registro de sobreposição'
                : `${group.registros.length} registros de sobreposição`}
            </Typography>

            {group.registros.map((item, itemIndex) => {
              const entries = Object.entries(item || {})
                .filter(
                  ([field]) => field !== 'geojson' && field !== 'geometry' && field !== 'feature',
                )
                .filter(([field]) => isFieldEnabled('sobreposicao', themeKey, field));

              return (
                <Card
                  key={`${group.cod_imovel || index}-${themeKey}-${itemIndex}`}
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: 0,
                    borderLeft: 3,
                    borderLeftColor: 'primary.main',
                    bgcolor: 'grey.50',
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {`${themeLabel} • Item ${itemIndex + 1}`}
                    </Typography>

                    {entries.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem detalhes disponíveis para este registro.
                      </Typography>
                    ) : (
                      <Grid container spacing={1.5}>
                        {entries.map(([field, value]) => (
                          <Grid item xs={12} sm={6} md={4} key={field}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('sobreposicao', field, humanizeKey(field))}
                            </Typography>
                            <ReportStyledField
                              domainKey="sobreposicao"
                              fieldKey={field}
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatThemeValue(field, value)}
                            </ReportStyledField>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ))
      )}
    </Box>
  );
}
